// ══════════════════════════════════════════════════════════════════════════════════════════════
// LES GABARITS D'E-MAIL — écrire une fois, réutiliser.
//
// POURQUOI CET ÉCRAN (demande utilisateur) : « une page pour créer et modifier les templates d'e-mails
// génériques utilisés dans l'application — les e-mails pour les factures A4 mais aussi ceux du
// marketing ». Il reproduit le dessin de la maquette (`Sliderz Backoffice.dc.html`, « Gabarits d'e-mail ») :
// la bibliothèque rangée par usage, l'éditeur en trois cartes, et l'APERÇU RENDU à côté.
//
// ⚠️ 🔴 L'USAGE DÉCIDE DES JETONS PROPOSÉS, et c'est le serveur qui le dit — avec, pour chaque jeton, son
// repli, son obligation et sa valeur d'exemple, et pour chaque usage s'il part HABILLÉ (carte, titre).
// Les deviner ici ferait proposer un jeton que l'envoi ne remplit pas — et il partirait EN TOUTES LETTRES.
//
// ⚠️ 🔴 LES PROBLÈMES SONT CEUX DU CŒUR (`templateProblems`), pendant la frappe : l'écran n'annonce pas
// « prêt » un gabarit que le serveur refuserait, ni l'inverse. Il n'écrit que les PHRASES (traduites).
//
// ⚠️ 🔴 L'APERÇU PASSE PAR LES DEUX PORTES DU CŒUR, celles de l'envoi : une campagne part telle que le
// commerçant l'écrit (`mergeHtml` sur `composeBody` — le bouton y est un LIEN, l'assainisseur n'admettant
// aucun style), un message habillé part en carte. Dessiner une pastille pour une campagne promettrait un
// bouton que le client ne recevra pas (§23.0) — c'est le seul écart avec le dessin, et il est écrit.
//
// ⚠️ L'ÉDITEUR EST LE COMPOSANT PARTAGÉ (`@orgazone/ui-web`), habillage « carte » : deux éditeurs
// divergeraient à la première correction. ⚠️ Il est NON CONTRÔLÉ : on le REMONTE avec une `key` pour
// charger un autre gabarit ou changer d'usage (les jetons changent avec lui).
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { ApiError } from '@orgazone/api-envelope/client';
import {
  composeBody, mergeHtml, mergeText, templateProblems, type MergeField, type MergeValues, type TemplateProblem,
} from '@orgazone/email-template';
import {
  DANGER_SOFT_BORDER, EmailEditor, Icon, OZ_INTERACTIVE, OZ_INTERACTIVE_SURFACE, Tooltip, Trans, alert, confirm, rovingRadio, showToast, useLatest,
} from '@orgazone/ui-web';
import {
  api, type EmailTemplateField, type EmailTemplateInput, type EmailTemplatePurpose, type EmailTemplateRow,
} from '../api';
import { useStoreScope } from '../store-context';
import { key, t, type Translatable } from '../i18n';

/**
 * CE QUE L'ÉCRAN DIT D'UN USAGE — son nom, sa description, et ce qu'il dit quand rien ne l'envoie.
 * ⚠️ 🔴 ICI ET NON AU SERVEUR : une phrase rendue par le serveur ne passe par aucun catalogue de traduction.
 * Le serveur envoie la CLÉ (`purpose`), l'écran nomme — le partage déjà retenu pour `whyCode`.
 */
const USAGES: Record<string, { l: Translatable; d: Translatable; unwired?: Translatable }> = {
  campaign: {
    l: key('Campagne'),
    d: key('Les e-mails du marketing — envoyés en masse, corps du commerçant sans habillage.'),
  },
  invoice: {
    l: key('Facture'),
    d: key('Le courrier qui accompagne une facture A4 — un client à la fois, habillé aux couleurs du magasin.'),
    unwired: key('Aucun envoi ne consomme encore ce gabarit : le courrier de facture reste à construire. Ce que vous écrivez ici est conservé et vous attendra.'),
  },
};

/** Ce qu'un usage non branché doit dire, quand l'écran ne lui connaît pas de phrase propre. */
const SANS_ENVOI = key('Aucun envoi ne consomme encore ce gabarit : le courrier correspondant reste à construire. Ce que vous écrivez ici est conservé et vous attendra.');

/** Le nom d'un usage, à l'écran — sa clé brute s'il est inconnu (un usage ajouté au serveur avant l'écran). */
const nomUsage = (purpose: string): string => (USAGES[purpose] ? t(USAGES[purpose]!.l) : purpose);

/** Le brouillon affiché — ce que l'opérateur est en train d'écrire. */
interface Brouillon {
  name: string;
  purpose: string;
  subject: string;
  title: string;
  body: string;
  btnOn: boolean;
  btnL: string;
  btnU: string;
}

/** Le brouillon d'un gabarit existant, ou d'un gabarit neuf. */
function brouillonDe(row: EmailTemplateRow | null, purpose: string): Brouillon {
  return row
    ? {
      name: row.name, purpose: row.purpose, subject: row.subject, title: row.title ?? '', body: row.body,
      btnOn: row.button !== null, btnL: row.button?.label ?? '', btnU: row.button?.url ?? '',
    }
    : { name: '', purpose, subject: '', title: '', body: '', btnOn: false, btnL: '', btnU: '' };
}

/** Ce que l'écran envoie — le titre seulement pour un usage habillé, le bouton seulement s'il est allumé. */
function saisieDe(d: Brouillon, dressed: boolean): EmailTemplateInput {
  return {
    name: d.name.trim(),
    purpose: d.purpose,
    subject: d.subject,
    title: dressed ? titreSaisi(d.title) : null,
    body: d.body,
    button: boutonDe(d.btnL, d.btnU, d.btnOn),
  };
}

/**
 * LE BROUILLON DIFFÈRE-T-IL DE CE QUI EST RANGÉ ?
 * ⚠️ On compare ce qui PARTIRAIT (la saisie), pas les champs cachés : changer d'usage puis revenir ne doit
 * pas laisser « Non enregistré » allumé pour un titre que l'usage n'affiche plus.
 */
export function estModifie(row: EmailTemplateRow | null, d: Brouillon, dressed: boolean): boolean {
  if (!row) return true;
  const s = saisieDe(d, dressed);
  return s.name !== row.name || s.purpose !== row.purpose || s.subject !== row.subject
    || s.title !== row.title || s.body !== row.body
    || JSON.stringify(s.button) !== JSON.stringify(row.button);
}

/** Les champs de l'usage, tels que le cœur les attend. */
const champsDe = (u: EmailTemplatePurpose | null): MergeField[] =>
  (u?.fields ?? []).map((f) => ({
    name: nomDuJeton(f), description: f.description, fallback: f.fallback, required: f.required,
  }));

/** Le nom d'un jeton, sans ses accolades. */
const nomDuJeton = (f: EmailTemplateField): string => f.token.replace(/^\{|\}$/g, '');

/** La première lettre en minuscule — un nom d'usage ou une description repris au milieu d'une phrase. */
const minus = (s: string): string => (s === '' ? s : s.charAt(0).toLocaleLowerCase() + s.slice(1));

/** Un problème, tel que l'écran le montre — sa marque, sa phrase, et son ton. */
interface ProblemeAffiche { ic: string; m: string; ton: 'rose' | 'amber' }

/**
 * LA PHRASE DE CHAQUE PROBLÈME DU CŒUR.
 * ⚠️ L'adresse du bouton suit la règle RÉELLE du cœur (`urlSure` : https, http, mailto, tel — ou un jeton
 * seul) : le dessin disait « https:// » seulement, ce qui aurait refusé à l'écran ce que le serveur accepte.
 */
export function problemeAffiche(p: TemplateProblem, usage: string): ProblemeAffiche {
  const liste = (noms: string[]) => noms.map((n) => `{${n}}`).join(', ');
  switch (p.code) {
    case 'unknown_field':
      return {
        ic: '✕', ton: 'rose',
        m: t('Jeton inconnu pour une {usage} : {jetons} — il partirait en toutes lettres. Retirez-le, ou changez d’usage.',
          { usage: minus(nomUsage(usage)), jetons: liste(p.tokens) }),
      };
    case 'missing_field':
      return {
        ic: '!', ton: 'amber',
        m: t('Ce courrier ne veut rien dire sans {jetons} — le repli ne remplace pas un jeton qu’on a oublié d’écrire.',
          { jetons: liste(p.tokens) }),
      };
    case 'empty_body':
      return { ic: '!', ton: 'amber', m: t('Le corps est vide : un gabarit sans texte n’imprimerait rien.') };
    case 'invalid_button':
      return p.reason === 'incomplete'
        ? { ic: '!', ton: 'amber', m: t('Un bouton, c’est ses deux moitiés : texte ET adresse — ou rien.') }
        : { ic: '!', ton: 'amber', m: t('L’adresse du bouton doit commencer par https://, http://, mailto: ou tel: — ou être un jeton seul.') };
  }
}

const TONS: Record<ProblemeAffiche['ton'], CSSProperties> = {
  rose: { background: 'var(--rose-bg)', border: `1px solid ${DANGER_SOFT_BORDER}`, color: 'var(--rose-fg)' },
  amber: { background: 'var(--amber-bg)', border: '1px solid var(--amber)', color: 'var(--amber-fg)' },
};

/**
 * LE NOM D'UNE COPIE — « X (copie) », puis « X (copie 2) »… jusqu'à un nom libre pour cet usage.
 * ⚠️ Le serveur refuse un nom déjà pris pour l'usage : proposer « X (copie) » une seconde fois ferait
 * échouer la duplication sans que l'opérateur ait rien fait de travers.
 */
export function nomDeCopie(nom: string, pris: readonly string[]): string {
  const occupe = new Set(pris);
  const premier = t('{v} (copie)', { v: nom });
  if (!occupe.has(premier)) return premier;
  for (let n = 2; ; n++) {
    const suivant = t('{v} (copie {k})', { v: nom, k: n });
    if (!occupe.has(suivant)) return suivant;
  }
}

/** Les initiales d'un magasin — l'avatar de l'expéditeur dans l'aperçu. */
export function initialesDe(nom: string): string {
  const mots = nom.trim().split(/\s+/).filter(Boolean);
  if (mots.length === 0) return '?';
  if (mots.length === 1) return mots[0]!.slice(0, 2).toLocaleUpperCase();
  return (mots[0]!.charAt(0) + mots[1]!.charAt(0)).toLocaleUpperCase();
}

// ── Les mesures du dessin ──────────────────────────────────────────────────────────────────────
const CARTE: CSSProperties = {
  background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden',
};
const ETIQ: CSSProperties = {
  display: 'block', fontSize: 'var(--t-note)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5,
};
const ETIQ_SUITE: CSSProperties = { color: 'var(--ink-400)' };
const CHAMP: CSSProperties = {
  width: '100%', height: 38, minHeight: 38, padding: '0 12px', border: '1px solid var(--ink-200)',
  borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: 'var(--t-lead)', color: 'var(--ink-900)',
  outline: 'none', boxSizing: 'border-box',
};
const SURTITRE: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em',
  textTransform: 'uppercase', color: 'var(--ink-500)',
};

/** Le segment du dessin (fond clair et ombre sur le choix actif). */
const segment = (on: boolean): CSSProperties => ({
  height: 26, padding: '0 9px', border: 'none', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-note)', fontWeight: 600,
  background: on ? 'var(--paper)' : 'transparent', color: on ? 'var(--ink-900)' : 'var(--ink-500)',
  boxShadow: on ? '0 1px 4px rgba(12,14,16,.14)' : 'none', cursor: 'pointer',
});

/** L'écran. */
export function GabaritsEmail() {
  // ⚠️ 🔴 LE GLN VIT SOUS `view.active`, PAS À LA RACINE du contexte. Il sert de CLÉ de rechargement : la
  //    bibliothèque est celle du magasin ACTIF, et garder celle d'avant afficherait les gabarits d'un
  //    autre commerçant. Le NOM sert à l'aperçu : c'est l'expéditeur que le client verra.
  const actif = useStoreScope().view?.active ?? null;
  const gln = actif?.gln ?? null;
  const nomMagasin = actif?.name ?? '';
  const [usages, setUsages] = useState<EmailTemplatePurpose[] | null>(null);
  const [rows, setRows] = useState<EmailTemplateRow[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  /** L'identifiant ouvert, `''` pour un gabarit NEUF, `null` quand rien n'est ouvert. */
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  /** Remonte l'éditeur — il est non contrôlé, donc un AUTRE texte exige un autre montage. */
  const [graine, setGraine] = useState(0);
  const [q, setQ] = useState('');
  const [manquantes, setManquantes] = useState(false);
  const [enCours, setEnCours] = useState(false);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT LA LISTE : les gabarits de l'ancien magasin, revenus après ceux du nouveau,
  //    s'ouvriraient sous son bandeau — et une modification partirait vers un gabarit que ce magasin n'a pas.
  const latest = useLatest();
  const charger = useCallback(() => {
    const isLatest = latest();
    Promise.all([api.emailTemplates(), api.emailTemplatePurposes()])
      .then(([l, u]) => { if (!isLatest()) return; setRows(l.rows); setUsages(u.rows); setErreur(null); })
      // ⚠️ LE MESSAGE RÉEL, et aucune vue de repli : une liste vide parce que la requête a échoué dit
      //    exactement le contraire de ce qui se passe (§23.0).
      .catch((e: unknown) => { if (!isLatest()) return; setRows(null); setErreur(e instanceof Error ? e.message : String(e)); });
  }, [latest]);

  // ⚠️ RECHARGÉ QUAND LE MAGASIN CHANGE, et ce qui était ouvert se referme : c'était un gabarit d'un autre.
  useEffect(() => { setOuvert(null); setBrouillon(null); charger(); }, [charger, gln]);

  const row = useMemo(
    () => (ouvert ? rows?.find((r) => r.id === ouvert) ?? null : null),
    [rows, ouvert],
  );
  const usage = useMemo(
    () => (brouillon ? usages?.find((u) => u.purpose === brouillon.purpose) ?? null : null),
    [usages, brouillon],
  );
  const dressed = usage?.dressed ?? false;
  const champs = useMemo(() => champsDe(usage), [usage]);
  const problemes = useMemo(
    () => {
      if (!brouillon || !usage) return [];
      const s = saisieDe(brouillon, dressed);
      return templateProblems({ subject: s.subject, title: s.title, body: s.body, button: s.button }, champs);
    },
    [brouillon, usage, dressed, champs],
  );
  const modifie = brouillon !== null && estModifie(row, brouillon, dressed);
  const peutEnregistrer = brouillon !== null && modifie && brouillon.name.trim() !== '' && problemes.length === 0
    && !enCours;

  const ouvrir = (r: EmailTemplateRow) => {
    setOuvert(r.id);
    setBrouillon(brouillonDe(r, r.purpose));
    setGraine((g) => g + 1);
  };

  const nouveau = () => {
    setOuvert('');
    setBrouillon(brouillonDe(null, usages?.[0]?.purpose ?? 'campaign'));
    setGraine((g) => g + 1);
  };

  const maj = (o: Partial<Brouillon>) => setBrouillon((d) => (d ? { ...d, ...o } : d));

  const changerUsage = (purpose: string) => {
    if (!brouillon || brouillon.purpose === purpose) return;
    maj({ purpose });
    // ⚠️ L'ÉDITEUR EST REMONTÉ : ses pastilles sont celles du nouvel usage. Le corps saisi est conservé.
    setGraine((g) => g + 1);
  };

  const enregistrer = () => {
    if (!brouillon || !peutEnregistrer) return;
    const existant = row;
    setEnCours(true);
    const saisie = saisieDe(brouillon, dressed);
    (existant ? api.updateEmailTemplate(existant.id, saisie) : api.createEmailTemplate(saisie))
      .then((r) => {
        setOuvert(r.record.id);
        setBrouillon(brouillonDe(r.record, r.record.purpose));
        setRows((l) => (existant
          ? (l ?? []).map((x) => (x.id === r.record.id ? r.record : x))
          : [...(l ?? []), r.record]));
        showToast(existant
          ? t('Gabarit enregistré — proposé tel quel au prochain envoi')
          : t('Gabarit créé — il vous attend dans la bibliothèque'), 'ok');
      })
      .catch((e: unknown) => {
        // ⚠️ UN NOM DÉJÀ PRIS SE DIT COMME UN NOM DÉJÀ PRIS : la seule réponse est de renommer. Tout autre
        //    refus porte le MOTIF RÉEL du serveur — un « échec » nu enverrait chercher une panne.
        if (e instanceof ApiError && e.key === 'name_taken') {
          void alert({
            title: t('Ce nom est déjà pris'),
            message: t('Deux gabarits du même nom ne se distinguent pas au moment de choisir. Renommez celui-ci.'),
            closeLabel: t('Compris'), tone: 'info',
          });
          return;
        }
        showToast(e instanceof Error ? e.message : String(e), 'err', 8000);
      })
      .finally(() => setEnCours(false));
  };

  const fermer = () => {
    if (!brouillon) return;
    // ⚠️ UN GABARIT NEUF ENCORE VIERGE SE FERME SANS QUESTION : annoncer « vos modifications seront perdues »
    //    quand il n'y en a aucune serait une phrase fausse.
    const vierge = !row && brouillon.name === '' && brouillon.body === '' && brouillon.subject === '';
    if (!modifie || vierge) {
      setOuvert(null); setBrouillon(null);
      return;
    }
    void confirm({
      title: t('Fermer sans enregistrer ?'),
      message: t('Vos modifications sur « {v} » seront perdues.', { v: brouillon.name.trim() || t('ce gabarit') }),
      confirmLabel: t('Fermer'), cancelLabel: t('Continuer à écrire'), tone: 'danger',
    }).then((ok: boolean) => { if (ok) { setOuvert(null); setBrouillon(null); } });
  };

  const dupliquer = () => {
    if (!row) return;
    const nom = nomDeCopie(row.name, (rows ?? []).filter((r) => r.purpose === row.purpose).map((r) => r.name));
    api.createEmailTemplate({
      name: nom, purpose: row.purpose, subject: row.subject, title: row.title, body: row.body, button: row.button,
    })
      .then((r) => {
        setRows((l) => [...(l ?? []), r.record]);
        ouvrir(r.record);
        showToast(t('Copie créée — « {v} »', { v: r.record.name }), 'ok');
      })
      .catch((e: unknown) => showToast(e instanceof Error ? e.message : String(e), 'err', 8000));
  };

  const supprimer = () => {
    if (!row) return;
    void confirm({
      title: t('Supprimer « {v} » ?', { v: row.name }),
      // ⚠️ 🔴 CE QUE LA SUPPRESSION NE CASSE PAS SE DIT : un gabarit est COPIÉ au moment où l'on s'en sert,
      //    jamais référencé. Sans cette phrase, on hésite à supprimer.
      message: t('Les messages déjà écrits ou envoyés ne changent pas : un gabarit est recopié au moment où on s’en sert.'),
      confirmLabel: t('Supprimer'), cancelLabel: t('Annuler'), tone: 'danger',
    }).then((ok: boolean) => {
      if (!ok) return;
      api.deleteEmailTemplate(row.id)
        .then((r) => {
          setOuvert(null); setBrouillon(null);
          setRows((l) => (l ?? []).filter((x) => x.id !== row.id));
          showToast(messageDeSuppression(r.removed), 'ok');
        })
        .catch((e: unknown) => showToast(e instanceof Error ? e.message : String(e), 'err', 8000));
    });
  };

  if (erreur) {
    return <p role="alert" style={{ margin: 0, color: 'var(--ink-900)', whiteSpace: 'pre-wrap' }}>{erreur}</p>;
  }
  if (!rows || !usages) return <div style={{ color: 'var(--ink-500)' }}>{t('Lecture…')}</div>;

  const cherche = q.trim().toLocaleLowerCase();
  const groupes = usages.map((u) => ({
    usage: u,
    rows: rows.filter((r) => r.purpose === u.purpose
      && (cherche === '' || `${r.name} ${r.subject}`.toLocaleLowerCase().includes(cherche))),
  }));

  return (
    <div data-screen-label="Gabarits d'e-mail">
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>
            {t('Gabarits d’e-mail')}
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            <Trans
              k="Écrire une fois, réutiliser : les textes de vos <1>campagnes</1> et de vos <2>documents</2>. Les jetons entre accolades se remplissent à l’envoi, client par client."
              parts={[fort, fort]}
            />
          </p>
        </div>
        <button type="button" onClick={nouveau} className={OZ_INTERACTIVE} style={{
          height: 40, padding: '0 17px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)',
          color: 'var(--paper)', fontSize: 'var(--t-title)', fontWeight: 700, display: 'inline-flex', alignItems: 'center',
          gap: 8, whiteSpace: 'nowrap', cursor: 'pointer',
        }}>
          <Icon name="plus" size={15} stroke={2.4} />{t('Nouveau gabarit')}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* ── La bibliothèque ─────────────────────────────────────────────────────────── */}
        <div style={{ ...CARTE, flex: '1 1 272px', maxWidth: 320, minWidth: 230 }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--ink-200)' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, height: 34, padding: '0 10px',
              border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-50)',
            }}>
              <span style={{ color: 'var(--ink-400)', display: 'inline-flex' }}><Icon name="search" size={14} stroke={2} /></span>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Chercher un gabarit…')}
                aria-label={t('Chercher un gabarit…')}
                style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: 'var(--t-body)', color: 'var(--ink-900)', outline: 'none' }} />
            </div>
          </div>
          <div>
            {groupes.map(({ usage: u, rows: liste }) => (
              <div key={u.purpose}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 14px 5px' }}>
                  <span style={SURTITRE}>{nomUsage(u.purpose)}</span>
                  <span style={{ width: 6, height: 6, borderRadius: 'var(--r-pill)', background: u.wired ? 'var(--teal-500)' : 'var(--amber)' }} />
                  <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)' }}>
                    {u.wired ? t('envois branchés') : t('envoi à venir')}
                  </span>
                </div>
                {liste.map((r) => {
                  const choisi = ouvert === r.id;
                  return (
                    <button key={r.id} type="button" onClick={() => ouvrir(r)} className={OZ_INTERACTIVE_SURFACE} style={{
                      display: 'block', width: '100%', padding: '9px 14px', border: 'none',
                      borderLeft: `3px solid ${choisi ? 'var(--teal-500)' : 'transparent'}`,
                      background: choisi ? 'var(--teal-100)' : 'transparent', textAlign: 'left', cursor: 'pointer',
                    }}>
                      <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
                      <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.subject}</span>
                    </button>
                  );
                })}
                {liste.length === 0 ? (
                  <div style={{ padding: '6px 14px 10px', fontSize: 'var(--t-note)', color: 'var(--ink-400)' }}>{t('Aucun gabarit')}</div>
                ) : null}
              </div>
            ))}
          </div>
          <div style={{ padding: '10px 14px', borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
            <Trans
              k="Un gabarit est <1>recopié</1> dans la campagne au moment où l’on s’en sert : le modifier ou le supprimer ne change rien de ce qui est déjà parti."
              parts={[fort]}
            />
          </div>
        </div>

        {brouillon === null ? (
          <div style={{ ...CARTE, flex: '1 1 520px', minWidth: 0, padding: '46px 24px', textAlign: 'center' }}>
            <span style={{
              width: 46, height: 46, borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', color: 'var(--teal-800)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}><Icon name="mail" size={22} stroke={2} /></span>
            <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)', marginTop: 12 }}>
              {t('Choisissez un gabarit, ou créez-en un')}
            </div>
            <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4, lineHeight: 1.5, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
              {t('Son texte sera proposé au prochain envoi — campagne ou courrier de facture — et rempli client par client.')}
            </div>
          </div>
        ) : (
          <>
            <Editeur
              brouillon={brouillon} usages={usages} usage={usage} dressed={dressed} graine={graine} ouvert={ouvert}
              problemes={problemes} modifie={modifie} existe={row !== null} peutEnregistrer={peutEnregistrer}
              maj={maj} changerUsage={changerUsage} enregistrer={enregistrer} fermer={fermer}
              dupliquer={dupliquer} supprimer={supprimer}
            />
            <Apercu
              brouillon={brouillon} usage={usage} dressed={dressed} champs={champs} manquantes={manquantes}
              setManquantes={setManquantes} nomMagasin={nomMagasin}
            />
          </>
        )}
      </div>
    </div>
  );
}

/** Le gras du dessin, dans une phrase traduite. */
const fort = (c: string): ReactNode => <strong style={{ color: 'var(--ink-700)' }}>{c}</strong>;

interface EditeurProps {
  brouillon: Brouillon;
  usages: EmailTemplatePurpose[];
  usage: EmailTemplatePurpose | null;
  dressed: boolean;
  graine: number;
  ouvert: string | null;
  problemes: TemplateProblem[];
  modifie: boolean;
  existe: boolean;
  peutEnregistrer: boolean;
  maj: (o: Partial<Brouillon>) => void;
  changerUsage: (purpose: string) => void;
  enregistrer: () => void;
  fermer: () => void;
  dupliquer: () => void;
  supprimer: () => void;
}

/** L'éditeur — l'identité et l'usage, le corps, le bouton d'action, puis les gestes. */
function Editeur(p: EditeurProps) {
  const d = p.brouillon;
  const u = p.usage;
  const unwired = u && !u.wired ? (USAGES[u.purpose]?.unwired ?? SANS_ENVOI) : null;
  const aide = u
    ? `${u.fields.map((f) => `${f.token} — ${minus(t(f.description))}`).join(' · ')}. ${t('Quand la valeur manque, le jeton disparaît proprement (« Bonjour, » sans prénom) — jamais d’accolade dans un e-mail.')}`
    : '';
  return (
    <div style={{ flex: '1 1 440px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={CARTE}>
        <div style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 12 }}>
          <label>
            <span style={ETIQ}>{t('Nom du gabarit')}</span>
            <input className="field-input" value={d.name} onChange={(e) => p.maj({ name: e.target.value })}
              placeholder={t('Ce que vous relirez pour le choisir — ex. Soldes d’hiver')} style={{ ...CHAMP, fontWeight: 600 }} />
          </label>
          <div>
            <span style={ETIQ}>{t('Usage')} <span style={ETIQ_SUITE}>{t('— il décide des jetons disponibles')}</span></span>
            <div role="radiogroup" aria-label={t('Usage')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 9 }}>
              {p.usages.map((x, i) => {
                const on = x.purpose === d.purpose;
                return (
                  <button key={x.purpose} type="button" role="radio" aria-checked={on} onClick={() => p.changerUsage(x.purpose)}
                    {...rovingRadio({ index: i, count: p.usages.length, checked: on, anyChecked: p.usages.some((u) => u.purpose === d.purpose), onPick: (k) => p.changerUsage(p.usages[k]!.purpose) })}
                    className={OZ_INTERACTIVE_SURFACE} style={{
                      display: 'flex', flexDirection: 'column', gap: 4, padding: '11px 12px', textAlign: 'left', cursor: 'pointer',
                      border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)',
                      background: on ? 'var(--teal-100)' : 'var(--paper)',
                    }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%' }}>
                      <span style={{
                        width: 15, height: 15, borderRadius: 'var(--r-pill)', border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box', background: 'var(--paper)',
                      }}>
                        {on ? <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)' }} /> : null}
                      </span>
                      <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{nomUsage(x.purpose)}</span>
                      <span style={{
                        marginLeft: 'auto', height: 18, display: 'inline-flex', alignItems: 'center', padding: '0 7px',
                        borderRadius: 'var(--r-pill)', fontSize: 'var(--t-micro)', fontWeight: 800, letterSpacing: '.03em',
                        whiteSpace: 'nowrap', textTransform: 'uppercase',
                        background: x.wired ? 'var(--teal-100)' : 'var(--amber-bg)', color: x.wired ? 'var(--teal-800)' : 'var(--amber-fg)',
                      }}>{x.wired ? t('Envoyé') : t('À venir')}</span>
                    </span>
                    <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.45 }}>
                      {USAGES[x.purpose] ? t(USAGES[x.purpose]!.d) : ''}
                    </span>
                  </button>
                );
              })}
            </div>
            {unwired ? (
              <div style={{
                marginTop: 9, padding: '9px 12px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)',
                color: 'var(--amber-fg)', fontSize: 'var(--t-dense)', lineHeight: 1.5,
              }}>{t(unwired)}</div>
            ) : null}
          </div>
          <label>
            <span style={ETIQ}>{t('Objet')}</span>
            <input className="field-input" value={d.subject} onChange={(e) => p.maj({ subject: e.target.value })}
              placeholder={t('Ce que le client lit avant d’ouvrir')} style={CHAMP} />
          </label>
          {p.dressed ? (
            <label>
              <span style={ETIQ}>{t('Titre de la carte')} <span style={ETIQ_SUITE}>{t('— facultatif, vide = pas de titre')}</span></span>
              <input className="field-input" value={d.title} onChange={(e) => p.maj({ title: e.target.value })}
                placeholder={t('Votre facture')} style={CHAMP} />
            </label>
          ) : null}
        </div>
      </div>

      <div style={CARTE}>
        {/* ⚠️ 🔴 REMONTÉ PAR SA `key` À CHAQUE CHANGEMENT DE GABARIT OU D'USAGE : l'éditeur est NON CONTRÔLÉ —
            réécrire son `innerHTML` à chaque frappe reposerait le curseur au début. */}
        <EmailEditor
          key={`${p.ouvert ?? ''}:${p.graine}`}
          look="card"
          initialValue={d.body}
          onChange={(html) => p.maj({ body: html })}
          fields={(u?.fields ?? []).map((f) => ({ token: f.token, required: f.required, tip: astuceDe(f) }))}
          ariaLabel={t('Corps du gabarit')}
        />
        {p.problemes.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 14px', borderTop: '1px solid var(--ink-100)' }}>
            {p.problemes.map((pb, i) => {
              const a = problemeAffiche(pb, d.purpose);
              return (
                <div key={i} style={{
                  ...TONS[a.ton], display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 11px',
                  borderRadius: 'var(--r-sm)', fontSize: 'var(--t-dense)', lineHeight: 1.45,
                }}>
                  <span style={{ fontWeight: 800, flexShrink: 0 }}>{a.ic}</span><span>{a.m}</span>
                </div>
              );
            })}
          </div>
        ) : null}
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
          {aide}
        </div>
      </div>

      <div style={CARTE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', flexWrap: 'wrap' }}>
          <button type="button" role="switch" aria-checked={d.btnOn} aria-label={t('Ajouter un bouton d’action')}
            onClick={() => p.maj({ btnOn: !d.btnOn })}
            style={{ border: 'none', background: 'transparent', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}>
            <span style={{
              width: 38, height: 22, borderRadius: 'var(--r-pill)', background: d.btnOn ? 'var(--teal-500)' : 'var(--ink-200)',
              display: 'flex', alignItems: 'center', justifyContent: d.btnOn ? 'flex-end' : 'flex-start', padding: '0 3px',
              flexShrink: 0, transition: 'background .15s', boxSizing: 'border-box',
            }}><span style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', background: 'var(--paper)' }} /></span>
            <span style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Bouton d’action')}</span>
          </button>
          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
            {d.btnOn
              ? t('Un seul bouton, sous le texte — vers votre site ou un jeton d’adresse.')
              : t('Aucun bouton : le message se termine sur le texte.')}
          </span>
        </div>
        {d.btnOn ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px,200px) minmax(0,1fr)', gap: 12, padding: '0 18px 16px' }}>
            <label>
              <span style={ETIQ}>{t('Texte du bouton')}</span>
              <input className="field-input" value={d.btnL} onChange={(e) => p.maj({ btnL: e.target.value })}
                placeholder={t('Voir la sélection')} style={CHAMP} />
            </label>
            <label>
              <span style={ETIQ}>{t('Adresse')} <span style={ETIQ_SUITE}>{t('— https:// ou un jeton')}</span></span>
              <input className="field-input" value={d.btnU} onChange={(e) => p.maj({ btnU: e.target.value })}
                placeholder="https://…" style={{ ...CHAMP, fontFamily: 'var(--font-mono)' }} />
            </label>
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
        <button type="button" onClick={p.enregistrer} disabled={!p.peutEnregistrer} className={OZ_INTERACTIVE} style={{
          height: 40, padding: '0 17px', border: 'none', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-title)', fontWeight: 700,
          background: p.peutEnregistrer ? 'var(--teal-500)' : 'var(--ink-200)',
          color: p.peutEnregistrer ? '#fff' : 'var(--ink-400)', cursor: p.peutEnregistrer ? 'pointer' : 'default',
        }}>{p.existe ? t('Enregistrer') : t('Créer le gabarit')}</button>
        <button type="button" onClick={p.fermer} style={{
          height: 40, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent',
          color: 'var(--ink-600)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: 'pointer',
        }}>{t('Fermer')}</button>
        {p.modifie ? (
          <span style={{
            height: 26, display: 'inline-flex', alignItems: 'center', padding: '0 10px', borderRadius: 'var(--r-pill)',
            background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-note)', fontWeight: 700,
          }}>{t('Non enregistré')}</span>
        ) : null}
        <span style={{ flex: 1 }} />
        {p.existe ? (
          <>
            <button type="button" onClick={p.dupliquer} className={OZ_INTERACTIVE_SURFACE} style={{
              height: 36, padding: '0 13px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-ctrl)',
              background: 'var(--paper)', color: 'var(--ink-800)', fontSize: 'var(--t-body)', fontWeight: 600, cursor: 'pointer',
            }}>{t('Dupliquer')}</button>
            <button type="button" onClick={p.supprimer} className={OZ_INTERACTIVE_SURFACE} style={{
              height: 36, padding: '0 13px', border: `1px solid ${DANGER_SOFT_BORDER}`, borderRadius: 'var(--r-ctrl)',
              background: 'var(--paper)', color: 'var(--rose-fg)', fontSize: 'var(--t-body)', fontWeight: 600, cursor: 'pointer',
            }}>{t('Supprimer')}</button>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** L'explication d'une pastille — ce que le champ vaut, s'il est exigé, et ce qui part quand il manque. */
function astuceDe(f: EmailTemplateField): string {
  const tete = f.required ? `${t(f.description)} — ${t('obligatoire')}` : t(f.description);
  const repli = f.fallback === '' ? t('repli : rien') : t('repli : « {v} »', { v: f.fallback });
  return `${tete} · ${repli}`;
}

interface ApercuProps {
  brouillon: Brouillon;
  usage: EmailTemplatePurpose | null;
  dressed: boolean;
  champs: MergeField[];
  manquantes: boolean;
  setManquantes: (v: boolean) => void;
  nomMagasin: string;
}

/**
 * L'APERÇU RENDU — ce que reçoit un client exemple, ou un client dont on n'a aucune valeur.
 * ⚠️ 🔴 PAR LES PORTES DE L'ENVOI : `mergeHtml` (campagne, corps nu — le bouton en LIEN, comme `composeBody`
 * le pose) ou la carte habillée. « Valeurs manquantes » montre le REPLI à l'œuvre, jamais une accolade.
 */
function Apercu({ brouillon: d, usage, dressed, champs, manquantes, setManquantes, nomMagasin }: ApercuProps) {
  const valeurs: MergeValues = manquantes
    ? {}
    : Object.fromEntries((usage?.fields ?? []).map((f) => [nomDuJeton(f), f.example]));
  const bouton = boutonDe(d.btnL, d.btnU, d.btnOn);
  const corps = dressed
    ? mergeHtml(d.body, champs, valeurs)
    : mergeHtml(composeBody({ body: d.body, title: null, button: bouton }), champs, valeurs);
  const sujet = mergeText(d.subject, champs, valeurs);
  const titre = dressed ? mergeText(d.title, champs, valeurs).trim() : '';
  const pilule = dressed && bouton !== null && bouton.label.trim() !== '' && bouton.url.trim() !== ''
    ? mergeText(bouton.label, champs, valeurs) : null;
  const texte = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  const destinataire = manquantes
    ? t('client sans prénom connu')
    : dressed ? t('m.dubois@exemple.fr') : t('camille.r@exemple.fr');

  return (
    <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10, position: 'sticky', top: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={SURTITRE}>{t('Aperçu')}</span>
        <span role="radiogroup" aria-label={t('Aperçu')} style={{
          display: 'inline-flex', padding: 2, gap: 2, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)',
        }}>
          <button type="button" role="radio" aria-checked={!manquantes} onClick={() => setManquantes(false)} style={segment(!manquantes)}
            {...rovingRadio({ index: 0, count: 2, checked: !manquantes, anyChecked: true, onPick: (k) => setManquantes(k === 1) })}>
            {t('Client exemple')}
          </button>
          <Tooltip content={t('Ce que reçoit un client dont on n’a pas la valeur — le repli, jamais l’accolade')}>
            <button type="button" role="radio" aria-checked={manquantes} onClick={() => setManquantes(true)} style={segment(manquantes)}
              {...rovingRadio({ index: 1, count: 2, checked: manquantes, anyChecked: true, onPick: (k) => setManquantes(k === 1) })}>
              {t('Valeurs manquantes')}
            </button>
          </Tooltip>
        </span>
      </div>
      <div style={CARTE}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ink-100)', background: 'var(--ink-50)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{
              width: 30, height: 30, borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--t-meta)', fontWeight: 800, flexShrink: 0,
            }}>{initialesDe(nomMagasin)}</span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>{nomMagasin}</span>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t('à {v}', { v: destinataire })}</span>
            </span>
          </div>
          <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)', marginTop: 9, lineHeight: 1.35 }}>
            {sujet.trim() || t('(sans objet)')}
          </div>
        </div>
        {/* ⚠️ LES COULEURS DE LA CARTE SONT CELLES DU MESSAGE, PAS DE L'INTERFACE : un e-mail se lit sur fond
            blanc chez le client, quel que soit le thème de celui qui compose — celles de `renderTemplate`. */}
        <div style={{ padding: 16, background: dressed ? '#f4f6f7' : '#fff' }}>
          <div style={{
            background: '#fff', borderRadius: dressed ? 16 : 0, border: dressed ? '1px solid #e2e6ea' : 'none',
            padding: dressed ? 26 : '4px 0',
          }}>
            {titre !== '' ? <div style={{ fontSize: 18, fontWeight: 700, color: '#0c0e10', margin: '0 0 12px' }}>{titre}</div> : null}
            {texte(corps) !== '' ? (
              <div style={{ fontSize: 'var(--t-control)', lineHeight: 1.6, color: '#22282c', overflowWrap: 'anywhere' }}
                dangerouslySetInnerHTML={{ __html: corps }} />
            ) : (
              <p style={{ margin: 0, fontSize: 'var(--t-control)', lineHeight: 1.6, color: texte(d.body) === '' ? 'var(--ink-400)' : 'var(--amber-fg)' }}>
                {texte(d.body) === ''
                  ? t('Le corps est vide.')
                  : t('Une fois les valeurs manquantes retirées, il ne reste rien à envoyer — le texte ne tient que par ses jetons.')}
              </p>
            )}
            {pilule !== null ? (
              <span style={{
                display: 'inline-block', marginTop: 14, padding: '11px 20px', borderRadius: 11, background: 'var(--teal-500)',
                color: '#fff', fontSize: 'var(--t-title)', fontWeight: 700,
              }}>{pilule}</span>
            ) : null}
          </div>
          {dressed ? (
            <div style={{ fontSize: 'var(--t-meta)', color: '#97a0a8', marginTop: 12 }}>{t('Message automatique — merci de ne pas répondre.')}</div>
          ) : null}
        </div>
      </div>
      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
        {dressed
          ? t('Un courrier de facture part habillé : carte, titre, pied « message automatique ». La pièce jointe A4 est un sujet à part.')
          : t('Une campagne part telle que vous l’écrivez — sans habillage. Un bouton d’action y devient un lien sous le texte.')}
      </div>
    </div>
  );
}

/**
 * CE QUE L’ÉCRAN DIT APRÈS UNE SUPPRESSION.
 *
 * ⚠️ 🔴 `removed: false` VEUT DIRE QU’IL N’Y AVAIT DÉJÀ PLUS RIEN À RETIRER (un autre onglet, un
 * double clic). Annoncer « supprimé » serait une ACTION INVENTÉE rendue comme réelle (§23.0) — et
 * l’opérateur croirait avoir agi sur quelque chose qui n’existait plus.
 */
export function messageDeSuppression(removed: boolean): string {
  return removed ? t('Gabarit supprimé') : t('Ce gabarit avait déjà été supprimé');
}

/**
 * LE TITRE SAISI — vide vaut ABSENCE de titre.
 * ⚠️ Une chaîne vide ferait poser au rendu un titre BLANC en tête du message, que le destinataire
 * verrait comme un trou. Le serveur applique la même règle ; celle-ci évite un aller-retour pour
 * afficher ce qui sera rangé.
 */
export function titreSaisi(v: string): string | null {
  return v.trim() === '' ? null : v;
}

/**
 * Le couple bouton, ou rien.
 * ⚠️ L'INTERRUPTEUR ÉTEINT VAUT « pas de bouton », quoi que contiennent les champs : ils restent remplis
 * pour qu'on puisse le rallumer sans tout ressaisir, mais rien ne part.
 * ⚠️ LES DEUX MOITIÉS VIDES VALENT AUSSI « pas de bouton » ; sinon on transmet ce qui est saisi, et c'est
 * le CŒUR partagé qui tranche (un bouton incomplet ou sans adresse sûre est REFUSÉ, et l'écran le dit
 * pendant la frappe). Décider ici aussi en ferait une seconde source de vérité.
 */
export function boutonDe(label: string, url: string, on = true): { label: string; url: string } | null {
  if (!on) return null;
  return label.trim() === '' && url.trim() === '' ? null : { label, url };
}
